import { ShefDescriptionChipsProps } from 'src/pages/consumer/order/components/ShefDescriptionCards/DescriptionCardsRow';
import { Chip, ChipColor } from 'src/shared/design-system/Chip';
import { Bowl, SpeechBubble } from 'src/shared/design-system/Icon';
import styled, { css } from 'styled-components';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { BoxShadow, Font, FontSize, Spacing } from 'web-common/src/shared/styles';

type ChipSize = 'medium' | 'large';
type ChipType = 'reviews' | 'deliveries' | 'foodSafetyCertified' | 'trialShef';

const DEFAULT_CHIP_SIZE: ChipSize = 'medium';
const CHIP_SIZE_TO_ICON_SIZE: Record<ChipSize, number> = {
  medium: 10,
  large: 12,
};
const CHIP_SIZE_TO_FONT_SIZE: Record<ChipSize, FontSize> = {
  medium: FontSize.XXS,
  large: FontSize.S,
};

interface BaseConsumerOrderShefChipProps {
  className?: string;
  size?: ChipSize;
}

export const ConsumerOrderShefChip = styled(Chip)<{
  size?: ChipSize;
  type?: ChipType;
  onClick?: () => void;
}>`
  box-shadow: none;
  font-size: ${({ size = DEFAULT_CHIP_SIZE }) => CHIP_SIZE_TO_FONT_SIZE[size]};
  font-weight: ${Font.Weight.SEMI_BOLD};
  line-height: 1;

  & > div {
    margin: 0 -1px;
  }

  ${({ size }) =>
    size === 'large'
      ? css`
          height: 35px;
        `
      : null}

  ${ifProp(
    'onClick',
    css`
      &:hover {
        cursor: pointer;
        box-shadow: ${BoxShadow.CHIP};
      }
    `
  )}
`;

const ChipIconContainer = styled.div`
  align-items: center;
  display: flex;
  margin-right: ${Spacing.QUARTER};
`;

interface ShefNumReviewsChipProps
  extends Pick<ShefDescriptionChipsProps, 'isTrialShef' | 'numRatings'>,
    BaseConsumerOrderShefChipProps {
  onReviewsClick?: () => void;
}

export const ShefNumReviewsChip = ({
  className,
  isTrialShef,
  numRatings,
  onReviewsClick,
  size = DEFAULT_CHIP_SIZE,
}: ShefNumReviewsChipProps): JSX.Element | null => {
  const iconSize = CHIP_SIZE_TO_ICON_SIZE[size];

  if (isTrialShef) {
    return <ConsumerOrderShefChip text='Trial Shef' color={ChipColor.DARK_PURPLE} className={className} size={size} />;
  }

  return numRatings >= 1 ? (
    <ConsumerOrderShefChip
      className={className}
      text={`${numRatings} ${numRatings === 1 ? 'review' : 'reviews'}`}
      color={ChipColor.LIGHT_LIGHT_YELLOW}
      icon={
        <ChipIconContainer>
          <SpeechBubble height={iconSize} width={iconSize} />
        </ChipIconContainer>
      }
      onClick={onReviewsClick}
      size={size}
    />
  ) : null;
};

export const ShefNumDeliveriesChip = ({
  className,
  size = DEFAULT_CHIP_SIZE,
  numOrders,
}: Pick<ShefDescriptionChipsProps, 'numOrders'> & BaseConsumerOrderShefChipProps): JSX.Element => {
  const iconSize = CHIP_SIZE_TO_ICON_SIZE[size];

  return (
    <ConsumerOrderShefChip
      className={className}
      text={`${numOrders} deliveries`}
      color={ChipColor.LIGHT_LIGHT_YELLOW}
      size={size}
      icon={
        <ChipIconContainer>
          <Bowl height={iconSize} width={iconSize} />
        </ChipIconContainer>
      }
    />
  );
};
